import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Skeleton,
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'
import {
  FilterPills,
  IFilterPillOption,
  Pagination,
  PdfPreviewDialog,
  SearchableFilterDropdown,
  SearchBar,
  SkeletonWrapper,
} from 'src/components/molecules'

import { InsightsMobileList } from './components/insights-mobile-list'
import { InsightsTable } from './components/insights-table'
import {
  INSIGHT_CATEGORY_ALL,
  InsightCategoryFilter,
  IUseMemberInsightsReturn,
} from './types'

interface IMemberInsightsTemplateProps {
  state: IUseMemberInsightsReturn
}

const ALL_PRODUCERS_VALUE = 'all'

const InsightsSectionSkeleton = (): JSX.Element => (
  <div className="flex flex-col gap-7">
    <Skeleton.InsightsTable rows={6} />
  </div>
)

export const MemberInsightsTemplate = ({
  state,
}: IMemberInsightsTemplateProps): JSX.Element => {
  const { t } = useTranslation()

  const {
    isLoading,
    isError,
    isEmpty,
    searchTerm,
    categoryFilter,
    categoryOptions,
    isCategoriesLoading,
    isCategoriesError,
    producerFilter,
    producerOptions,
    pageItems,
    currentPage,
    totalPages,
    selectedInsight,
    selectedInsightBlobUrl,
    setSearchTerm,
    setCategoryFilter,
    setProducerFilter,
    setCurrentPage,
    openInsightPreview,
    closeInsightPreview,
  } = state

  const pillOptions = useMemo<IFilterPillOption<InsightCategoryFilter>[]>(
    () => [
      { value: INSIGHT_CATEGORY_ALL, label: 'All' },
      ...categoryOptions.map(category => ({
        value: category,
        label: category,
      })),
    ],
    [categoryOptions],
  )

  const producerSelectOptions = useMemo(
    () => [
      { value: ALL_PRODUCERS_VALUE, label: 'All producers' },
      ...producerOptions.map(producer => ({
        value: producer,
        label: producer,
      })),
    ],
    [producerOptions],
  )

  const handleProducerChange = (value: string): void => {
    setProducerFilter(value === ALL_PRODUCERS_VALUE ? '' : value)
  }

  const showEmpty = !isLoading && !isError && isEmpty
  const showError = !isLoading && isError
  const showList = !isLoading && !isError && !isEmpty

  return (
    <main className="bg-surface-page">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-5 py-8 md:gap-10 md:px-12 md:py-11 xl:px-0">
        <header className="flex flex-col gap-5 md:gap-4">
          <Typography
            variant={TypographyVariant.headline}
            weight={TypographyWeight.light}
            className="text-content-primary"
          >
            Member insights
          </Typography>
          <div className="h-px w-full bg-content-secondary" />
        </header>

        <section className="flex flex-col gap-7 md:gap-10">
          <div className="flex h-11 w-full items-center gap-2 md:gap-4">
            <SearchBar
              name="member-insights-search"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search reports..."
              className="flex-1"
            />
            <SearchableFilterDropdown
              name="member-insights-producer-filter"
              value={
                producerFilter === '' ? ALL_PRODUCERS_VALUE : producerFilter
              }
              defaultValue={ALL_PRODUCERS_VALUE}
              onChange={handleProducerChange}
              options={producerSelectOptions}
              placeholder="All producers"
              className="w-[125px] md:w-[160px]"
            />
          </div>

          <div className="-mx-5 overflow-x-auto px-5 [&::-webkit-scrollbar]:hidden md:mx-0 md:overflow-visible md:px-0 [scrollbar-width:none]">
            {isCategoriesLoading ? (
              <div
                role="status"
                aria-label="Loading categories…"
                aria-busy="true"
                className="flex w-max gap-2 md:w-auto md:gap-4"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton.Base
                    key={i}
                    className="h-6 w-24 rounded-[12px] opacity-60"
                  />
                ))}
              </div>
            ) : isCategoriesError ? null : (
              <FilterPills<InsightCategoryFilter>
                ariaLabel="Insight categories"
                value={categoryFilter}
                options={pillOptions}
                onChange={setCategoryFilter}
                className="w-max gap-2 md:w-auto md:gap-4"
              />
            )}
          </div>

          {showError && (
            <div className="py-10 text-center">
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                {t('memberInsights.detail.loadErrorToastMessage')}
              </Typography>
            </div>
          )}

          {showEmpty && (
            <div className="py-10 text-center">
              <Typography
                variant={TypographyVariant.bodyXs}
                className="text-content-secondary"
              >
                No insights match your filters.
              </Typography>
            </div>
          )}

          <SkeletonWrapper
            isLoading={isLoading}
            skeleton={<InsightsSectionSkeleton />}
          >
            {showList && (
              <>
                <div className="flex flex-col items-end gap-6 md:hidden">
                  <InsightsMobileList
                    insights={pageItems}
                    onInsightClick={openInsightPreview}
                  />
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={setCurrentPage}
                  />
                </div>

                <div className="hidden w-full overflow-x-auto md:block">
                  <div className="min-w-[900px]">
                    <InsightsTable
                      insights={pageItems}
                      onRowClick={openInsightPreview}
                    />
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </SkeletonWrapper>
        </section>
      </div>

      {selectedInsight && (
        <PdfPreviewDialog
          isOpen
          onClose={closeInsightPreview}
          title={selectedInsight.reportName}
          subtitle={`${selectedInsight.category} · ${selectedInsight.producer} · ${selectedInsight.releaseDate}`}
          pdfUrl={selectedInsightBlobUrl}
        />
      )}
    </main>
  )
}
